import { isDev } from "@workwave-tidal/core/base";
import { castDraft, produce } from "@workwave-tidal/core/immer";
import { throwIfInvalidIndex } from "./utils/throwIfInvalidIndex.js";
import { throwIfNotArrayField } from "./utils/throwIfNotArrayField.js";
import { throwIfUnknownField } from "./utils/throwIfUnknownField.js";
const __DEV__ = isDev();
const prefixStyles = [
  "color: lightCoral",
  "font-size: 12px",
  "font-weight: bolder",
  "padding-left: 10px",
  "padding-right: 5px"
].join(";");
const contextStyles = ["color: goldenRod", "font-size: 12px", "font-weight: bolder", "padding-right: 10px"].join(";");
const emptyErrorArray = [];
function createForm(id, baseFields, config) {
  function isStrictMode() {
    return config?.mode === "strict";
  }
  function isDynamicMode() {
    return config?.mode !== "strict";
  }
  let debugMode = "silent";
  function log(context, ...args) {
    if (debugMode === "verbose") {
      console.log(`%c[${id}] %c${context}`, prefixStyles, contextStyles, ...args);
    }
  }
  function createStore() {
    const fields = baseFields ? produce(baseFields, (draft) => {
      if (config?.overrideValues) {
        for (const [key, value] of Object.entries(config.overrideValues)) {
          draft[key].value = value;
          draft[key].initialValue = value;
        }
      }
      for (const key of Object.keys(draft)) {
        draft[key].initialValue ??= draft[key].value;
        draft[key].visible ??= true;
        draft[key].disabled ??= false;
        draft[key].required ??= false;
        draft[key].validating ??= false;
        draft[key].status ??= "pristine";
      }
    }) : (
      // If `baseFields` is missing set the form to an empty object
      {}
    );
    const initialValues = {};
    let initialStatus = "pristine";
    for (const [key, field] of Object.entries(fields)) {
      initialValues[key] = field.value;
      initialStatus = field.status !== "pristine" ? "indeterminate" : initialStatus;
    }
    return {
      id,
      initialValues,
      values: { ...initialValues },
      fields,
      errors: {},
      meta: {
        disabled: false,
        submitting: false,
        validating: false,
        status: initialStatus
      }
    };
  }
  const store = createStore();
  function resetFieldInitialValues(mode, initialValues) {
    if (initialValues) {
      store.initialValues = produce(store.initialValues, (draft) => {
        for (const [fieldName, value] of Object.entries(initialValues)) {
          draft[fieldName] = value;
        }
      });
    }
    if (mode === "force") {
      store.fields = produce(store.fields, (draft) => {
        for (const [fieldName, field] of Object.entries(draft)) {
          field.value = store.initialValues[fieldName];
          field.status = api.getFieldErrors(fieldName).length > 0 ? "invalid" : "pristine";
        }
      });
      store.values = produce(store.values, (draft) => {
        for (const fieldName of Object.keys(draft)) {
          draft[fieldName] = store.fields[fieldName].value;
        }
      });
    }
  }
  const errorsByField = /* @__PURE__ */ new Map();
  let globalValidationRunning = false;
  const validatorsById = /* @__PURE__ */ new Map();
  const validatorIdByFunction = /* @__PURE__ */ new Map();
  const validatorsByField = /* @__PURE__ */ new Map();
  const activeAsyncValidation = /* @__PURE__ */ new Map();
  const runningValidationsByField = /* @__PURE__ */ new Map();
  let validationPending;
  let resolvePendingValidation = () => void 0;
  function waitForValidation() {
    if (!store.meta.validating) {
      const isValid = store.meta.status === "valid";
      log("resolvePendingValidation SYNC", isValid);
      if (validationPending) {
        resolvePendingValidation(isValid);
      }
      return Promise.resolve(isValid);
    }
    if (!validationPending) {
      validationPending = new Promise((resolve) => {
        resolvePendingValidation = (value) => {
          log("resolvePendingValidation ASYNC", value);
          resolve(value);
          resolvePendingValidation = () => void 0;
          validationPending = void 0;
        };
      });
    }
    return validationPending;
  }
  const formMutations = /* @__PURE__ */ new Map();
  const fieldMutations = /* @__PURE__ */ new Map();
  function applyMutations(initialChangedValues, fieldToSkip) {
    const changedValues = { ...initialChangedValues };
    for (const [targetFieldName, fieldMutation] of fieldMutations) {
      if (fieldToSkip && targetFieldName === fieldToSkip) continue;
      if (fieldMutation) {
        const mutatedValue = fieldMutation(api, changedValues, initialChangedValues, targetFieldName);
        if (!store.fields[targetFieldName]) {
          if (isStrictMode()) {
            throw new Error("In strict mode you cant create new fields dynamically");
          }
          throw new Error("Dynamic field creation from mutation is not supported yet");
        }
        if (store.fields[targetFieldName].value !== mutatedValue) {
          changedValues[targetFieldName] = mutatedValue;
        } else {
          delete changedValues[targetFieldName];
        }
      }
    }
    for (const [, formMutation] of formMutations) {
      const mutatedValues = formMutation(api, changedValues, initialChangedValues);
      if (mutatedValues) {
        for (const [targetFieldName, mutatedValue] of Object.entries(mutatedValues)) {
          if (store.fields[targetFieldName].value !== mutatedValue) {
            changedValues[targetFieldName] = mutatedValue;
          } else {
            delete changedValues[targetFieldName];
          }
        }
      }
    }
    return changedValues;
  }
  const fieldTransforms = /* @__PURE__ */ new Map();
  function applyTransforms(initialChangedValues, fieldToSkip) {
    const changedValues = { ...initialChangedValues };
    for (const [targetFieldName, fieldTransform] of fieldTransforms) {
      if (fieldToSkip && targetFieldName === fieldToSkip) continue;
      const initialChangedValue = initialChangedValues[targetFieldName];
      const changedValue = changedValues[targetFieldName];
      if (fieldTransform && changedValue && initialChangedValue) {
        const transformedValue = fieldTransform(api, changedValue, initialChangedValue, targetFieldName);
        if (!store.fields[targetFieldName]) {
          if (isStrictMode()) {
            throw new Error("In strict mode you cant create new fields dynamicaly");
          }
          throw new Error("Dynamic field creation from transform is not supported yet");
        }
        if (store.fields[targetFieldName].value !== transformedValue) {
          changedValues[targetFieldName] = transformedValue;
        } else {
          delete changedValues[targetFieldName];
        }
      }
    }
    return changedValues;
  }
  const fieldSubscribers = /* @__PURE__ */ new Set();
  const formSubscribers = /* @__PURE__ */ new Set();
  const formMetaSubscribers = /* @__PURE__ */ new Set();
  let notifyFormSubscribersPending;
  function notifyFormSubscribers() {
    if (!notifyFormSubscribersPending) {
      notifyFormSubscribersPending = Promise.resolve().then(() => {
        log("notifyFormSubscribers");
        formSubscribers.forEach((subscriber) => {
          subscriber(api);
        });
        notifyFormSubscribersPending = void 0;
      });
    }
    return notifyFormSubscribersPending;
  }
  let notifyMetaSubscribersPending;
  function notifyMetaSubscribers() {
    if (!notifyMetaSubscribersPending) {
      notifyMetaSubscribersPending = Promise.resolve().then(() => {
        const meta = api.getMeta();
        log("notifyMetaSubscribers");
        formMetaSubscribers.forEach((subscriber) => {
          subscriber(meta, api);
        });
        notifyMetaSubscribersPending = void 0;
      });
    }
    return notifyMetaSubscribersPending;
  }
  function notifyFieldSubscriber(fieldName, operations) {
    if (store.fields[fieldName]) {
      const field = api.getField(fieldName);
      const errors = api.getFieldErrors(fieldName);
      log("notifyFieldSubscriber", fieldName);
      fieldSubscribers.forEach(([subName, subscriber]) => {
        if (fieldName === subName) {
          subscriber(field, errors, api, operations);
        }
      });
    }
  }
  function notifyFieldSubscribers(fieldNames, arrayFieldOperations) {
    for (const fieldName of fieldNames) {
      const operations = arrayFieldOperations?.[fieldName];
      notifyFieldSubscriber(fieldName, operations);
    }
  }
  function refreshFormMetaValidatingState(silent) {
    const validating = globalValidationRunning || Object.values(store.fields).some((field) => field.validating);
    log("refreshFormMetaValidatingState");
    if (validating !== store.meta.validating) {
      store.meta = produce(store.meta, (draft) => {
        draft.validating = validating;
      });
      if (!silent) {
        notifyMetaSubscribers();
        notifyFormSubscribers();
      }
      return true;
    }
    return false;
  }
  function refreshFieldsStatus(fieldsToValidate, silent) {
    log("refreshFieldsStatus", fieldsToValidate);
    const reducedErrors = Object.values(store.errors).reduce((acc, error) => {
      if (fieldsToValidate.includes(error.field)) {
        acc[error.field] ??= true;
      }
      return acc;
    }, {});
    const prevStoreFields = store.fields;
    store.fields = produce(store.fields, (draft) => {
      for (const [fieldName, field] of Object.entries(draft)) {
        if (fieldsToValidate.includes(fieldName)) {
          const hasErrors = !!reducedErrors[fieldName];
          field.status = hasErrors ? "invalid" : "valid";
        }
      }
    });
    let changed = false;
    for (const [fieldName, field] of Object.entries(store.fields)) {
      const prevFieldStatus = prevStoreFields[fieldName].status;
      if (field.status !== prevFieldStatus) {
        changed = true;
        if (!silent) {
          notifyFieldSubscriber(fieldName);
        }
      }
    }
    if (changed && !silent) {
      notifyFormSubscribers();
    }
    return changed;
  }
  function updateStoreErrors(validation) {
    const affectedFieldNames = /* @__PURE__ */ new Set();
    store.errors = produce(store.errors, (draft) => {
      for (const [errorId, value] of Object.entries(validation)) {
        const currentError = draft[errorId];
        if (value) {
          affectedFieldNames.add(value.field);
        } else if (currentError && typeof currentError.field === "string") {
          affectedFieldNames.add(currentError.field);
        }
        if (value === null) {
          delete draft[errorId];
        } else {
          draft[errorId] = castDraft(value);
        }
      }
    });
    log("updateStoreErrors", affectedFieldNames);
    for (const fieldName of affectedFieldNames) {
      const errorsList = errorsByField.get(fieldName) ?? [];
      errorsByField.set(
        fieldName,
        produce(errorsList, (draft) => {
          for (const [errorId, value] of Object.entries(validation)) {
            const index = draft.findIndex((err) => err.id === errorId);
            if (index > -1) {
              draft.splice(index, 1);
            }
            if (value !== null && value.field === fieldName) {
              draft.push(castDraft(value));
            }
          }
          draft.sort(({ priority: prev = 0 }, { priority: next = 0 }) => {
            return next - prev;
          });
        })
      );
    }
    return Array.from(affectedFieldNames);
  }
  function refreshFormStatus(silent) {
    const meta = Object.values(store.fields).reduce(
      (acc, field) => {
        const { pristine, indeterminate, valid, invalid } = acc;
        return {
          valid: valid && field.status === "valid",
          invalid: invalid || field.status === "invalid",
          pristine: pristine && field.status === "pristine",
          indeterminate: indeterminate || field.status === "indeterminate"
        };
      },
      {
        pristine: true,
        indeterminate: false,
        valid: true,
        invalid: false
      }
    );
    const nextStatus = meta.pristine ? "pristine" : meta.valid ? "valid" : meta.invalid ? "invalid" : "indeterminate";
    log("refreshFormStatus", nextStatus);
    if (nextStatus !== store.meta.status) {
      store.meta = produce(store.meta, (draft) => {
        draft.status = nextStatus;
      });
      if (!silent) {
        notifyMetaSubscribers();
        notifyFormSubscribers();
      }
      return true;
    }
    return false;
  }
  const api = {
    // ------------------------------------------
    // UTILITIES
    // ------------------------------------------
    setDebugMode: (mode) => {
      debugMode = mode;
    },
    // ------------------------------------------
    // ERRORS
    // ------------------------------------------
    getErrors: () => {
      return store.errors;
    },
    // @ts-expect-error: TS is unable to reconcile ERROR[] with FormError<FIELD_NAME >[]
    getFieldErrors: (fieldName) => {
      const field = store.fields[fieldName];
      if (!field) {
        if (isStrictMode()) {
          throw new Error(
            `Trying to access an unknown field: ${String(fieldName)} with strict mode enabled. This is a no-op.`
          );
        }
        return emptyErrorArray;
      }
      const fieldErrors = errorsByField.get(fieldName) ?? [];
      errorsByField.set(fieldName, fieldErrors);
      return fieldErrors;
    },
    clearError: (...ids) => {
      log("clearErrorById", ids);
      const affectedFields = /* @__PURE__ */ new Set();
      function shouldClearError(id2) {
        return ids.length === 0 ? true : ids.includes(id2);
      }
      store.errors = produce(store.errors, (draft) => {
        for (const id2 of Object.keys(draft)) {
          const fieldName = draft[id2].field;
          if (shouldClearError(id2)) {
            delete draft[id2];
            affectedFields.add(fieldName);
            let fieldErrors = errorsByField.get(fieldName) ?? [];
            fieldErrors = fieldErrors.filter((error) => error.id !== id2);
            errorsByField.set(fieldName, fieldErrors);
          }
        }
      });
      store.fields = produce(store.fields, (draft) => {
        for (const [fieldName, field] of Object.entries(draft)) {
          if (affectedFields.has(fieldName)) {
            field.status = "indeterminate";
            notifyFieldSubscriber(fieldName);
          }
        }
      });
      refreshFormStatus();
      notifyFormSubscribers();
    },
    // ------------------------------------------
    // FIELDS
    // ------------------------------------------
    registerField: (fieldName, field) => {
      if (isStrictMode()) {
        throw new Error(`Trying to register a field: ${String(fieldName)} with strict mode enabled. This is a no-op.`);
      }
      if (store.fields[fieldName]) {
        return false;
      }
      store.fields = produce(store.fields, (draft) => {
        const draftFieldName = fieldName;
        const draftField = castDraft(field);
        draft[draftFieldName] = { ...draftField };
        draft[draftFieldName].initialValue ??= field.value;
        draft[draftFieldName].visible ??= true;
        draft[draftFieldName].disabled ??= false;
        draft[draftFieldName].required ??= false;
        draft[draftFieldName].validating ??= false;
        draft[draftFieldName].status ??= "pristine";
      });
      store.values = produce(store.values, (draft) => {
        Object.assign(draft, { [fieldName]: store.fields[fieldName].value });
      });
      notifyFieldSubscribers([fieldName]);
      refreshFormStatus();
      notifyMetaSubscribers();
      notifyFormSubscribers();
      return true;
    },
    deregisterField: (fieldName, destroy = false) => {
      if (isStrictMode()) {
        throw new Error(`Trying to deregister a field: ${String(fieldName)} with strict mode enabled. This is a no-op.`);
      }
      if (destroy) {
        for (const subscription of fieldSubscribers) {
          const [field] = subscription;
          if (field === fieldName) {
            fieldSubscribers.delete(subscription);
          }
        }
        const validators = validatorsByField.get(fieldName);
        if (validators) {
          for (const validator of validators) {
            const id2 = validatorIdByFunction.get(validator);
            if (id2) validatorsById.delete(id2);
            validatorIdByFunction.delete(validator);
          }
        }
        validatorsByField.delete(fieldName);
        fieldMutations.delete(fieldName);
        fieldTransforms.delete(fieldName);
      }
      store.fields = produce(store.fields, (draft) => {
        const draftFieldName = fieldName;
        delete draft[draftFieldName];
      });
      store.values = produce(store.values, (draft) => {
        const draftFieldName = fieldName;
        delete draft[draftFieldName];
      });
      store.initialValues = produce(store.initialValues, (draft) => {
        const draftFieldName = fieldName;
        delete draft[draftFieldName];
      });
      store.errors = produce(store.errors, (draft) => {
        for (const [errorId, error] of Object.entries(draft)) {
          if (error.field === fieldName) {
            delete draft[errorId];
          }
        }
      });
      errorsByField.delete(fieldName);
      refreshFormStatus();
      notifyMetaSubscribers();
      notifyFormSubscribers();
    },
    getField: (fieldName) => {
      if (store.fields[fieldName]) {
        return store.fields[fieldName];
      }
      if (isStrictMode()) {
        throw new Error(
          `Trying to access an unknown field: ${String(fieldName)} with strict mode enabled. This is a no-op.`
        );
      }
      throw new Error(
        "Trying to access an unknown field: ${String(fieldName)} with strict mode enabled. Not supported yet in dynamic mode"
      );
    },
    getValues: () => {
      return store.values;
    },
    getInitialValues: () => {
      return store.initialValues;
    },
    // ------------------------------------------
    // CHANGE VALUES
    // ------------------------------------------
    change: (fieldName, maybeLazyValue, operations) => {
      if (isStrictMode()) {
        throwIfUnknownField(store, fieldName);
      }
      const nextValue = typeof maybeLazyValue === "function" ? maybeLazyValue(store.values[fieldName], store.values) : maybeLazyValue;
      log("change", fieldName, nextValue);
      const initialChangedValues = {
        [fieldName]: nextValue
      };
      const changedValues = applyMutations(applyTransforms(initialChangedValues), fieldName);
      store.fields = produce(store.fields, (draft) => {
        for (const [targetFieldName, fieldValue] of Object.entries(changedValues)) {
          if (!draft[targetFieldName]) {
            draft[targetFieldName] = {
              value: fieldValue,
              initialValue: void 0,
              visible: true,
              disabled: false,
              required: false,
              validating: false,
              status: "indeterminate"
            };
          } else {
            draft[targetFieldName].value = fieldValue;
            draft[targetFieldName].status = "indeterminate";
          }
        }
      });
      store.values = produce(store.values, (draft) => {
        Object.assign(draft, changedValues);
      });
      const arrayFieldOperations = operations ? { [fieldName]: operations } : void 0;
      notifyFieldSubscribers(
        Object.keys(changedValues),
        arrayFieldOperations
      );
      refreshFormStatus();
      notifyFormSubscribers();
    },
    batch: (maybeLazyFields) => {
      const nextValues = typeof maybeLazyFields === "function" ? maybeLazyFields(store.values) : maybeLazyFields;
      log("batch", nextValues);
      const initialChangedValues = {};
      for (const [fieldName, value] of Object.entries(nextValues)) {
        if (isStrictMode()) {
          throwIfUnknownField(store, fieldName);
        }
        initialChangedValues[fieldName] = value;
      }
      const changedValues = applyMutations(applyTransforms(initialChangedValues));
      store.fields = produce(store.fields, (draft) => {
        for (const [targetFieldName, fieldValue] of Object.entries(changedValues)) {
          if (!draft[targetFieldName]) {
            draft[targetFieldName] = {
              value: fieldValue,
              initialValue: void 0,
              visible: true,
              disabled: false,
              required: false,
              validating: false,
              status: "indeterminate"
            };
          } else {
            draft[targetFieldName].value = fieldValue;
            draft[targetFieldName].status = "indeterminate";
          }
        }
      });
      store.values = produce(store.values, (draft) => {
        Object.assign(draft, changedValues);
      });
      notifyFieldSubscribers(Object.keys(changedValues));
      refreshFormStatus();
      notifyFormSubscribers();
    },
    // biome-ignore lint/style/useDefaultParameterLast: <explanation>
    reset: (mode = "preserve", initialValues, silent) => {
      log("reset", mode);
      if (mode === "force") {
        store.errors = {};
      }
      resetFieldInitialValues(mode, initialValues);
      refreshFormStatus(silent);
      if (!silent) {
        notifyFieldSubscribers(Object.keys(store.fields));
        notifyMetaSubscribers();
        notifyFormSubscribers();
      }
    },
    // -----------------------------------------------------------------
    // ARRAY API
    // -----------------------------------------------------------------
    /**
     * Changes the value of an element in an array field.
     *
     * @param fieldName The name of the target field (should be an array field).
     * @param index The index of the element to update inside the array.
     * @param maybeLazyValue New value or update function.
     */
    changeArrayElement: (fieldName, index, maybeLazyValue) => {
      throwIfUnknownField(store, fieldName);
      throwIfNotArrayField(store, fieldName);
      throwIfInvalidIndex(store.fields[fieldName].value, index);
      const value = store.fields[fieldName].value;
      log("changeArrayElement", fieldName, index, maybeLazyValue);
      const newValue = produce(value, (draft) => {
        draft[index] = typeof maybeLazyValue === "function" ? maybeLazyValue(draft[index], store.values) : maybeLazyValue;
      });
      const operations = [
        {
          type: "change",
          index
        }
      ];
      api.change(fieldName, newValue, operations);
    },
    /**
     * Removes an Element of an array field from a specified position.
     *
     * @param fieldName The name of the target field (should be an array field).
     * @param index The index of the value to remove.
     */
    removeArrayElements: (fieldName, index) => {
      throwIfUnknownField(store, fieldName);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName].value;
      throwIfInvalidIndex(value, index);
      log("removeArrayElements", fieldName, index);
      const sortedIndexes = Array.isArray(index) ? index.sort().reverse() : [index];
      const newValue = produce(value, (draft) => {
        for (const i of sortedIndexes) {
          draft.splice(i, 1);
        }
      });
      const operations = [
        {
          type: "remove",
          index: Array.isArray(index) ? index : [index]
        }
      ];
      api.change(fieldName, newValue, operations);
    },
    /**
     * Adds one or more elements in a specified position of the array field.
     *
     * @param fieldName The name of the target field (should be an array field).
     * @param index Index of the position where to add the Element.
     * @param elements The elements to add into the array.
     */
    addArrayElements: (fieldName, index, ...elements) => {
      throwIfUnknownField(store, fieldName);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName].value;
      if (index !== "end") {
        throwIfInvalidIndex(value, index);
      }
      log("addArrayElements", fieldName, index);
      const indexesToNotify = [];
      const newValue = produce(value, (draft) => {
        if (index === "end") {
          draft.push(...elements);
          indexesToNotify.push(draft.length - 1);
        } else {
          draft.splice(index, 0, ...elements);
          indexesToNotify.push(index);
        }
      });
      const start = index === "end" ? value.length : index;
      const operations = [
        {
          type: "add",
          index: Array.from({ length: elements.length }, (_, i) => start + i)
        }
      ];
      api.change(fieldName, newValue, operations);
    },
    /**
     * Swap the position of two elements inside an array field.
     *
     * @param fieldName The name of the target field (should be an array field).
     * @param indexA The index of the first element to swap
     * @param indexB The index of the second element to swap
     */
    swapArrayElements: (fieldName, indexA, indexB) => {
      throwIfUnknownField(store, fieldName);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName].value;
      throwIfInvalidIndex(store.fields[fieldName].value, indexA);
      throwIfInvalidIndex(store.fields[fieldName].value, indexB);
      log("swapArrayElements", fieldName, indexA, indexB);
      const newValue = produce(value, (draft) => {
        const temp = structuredClone(draft[indexA]);
        draft[indexA] = draft[indexB];
        draft[indexB] = temp;
      });
      const operations = [
        {
          type: "swap",
          index: [indexA, indexB]
        }
      ];
      api.change(fieldName, newValue, operations);
    },
    /**
     * Clears all the elements of an array field.
     *
     * @param fieldName The name of the target field (should be an array field).
     */
    clearArrayElements: (fieldName) => {
      throwIfUnknownField(store, fieldName);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName].value;
      log("clearArrayElements", fieldName);
      const operations = [
        {
          type: "remove",
          index: [...value.keys()]
        }
      ];
      api.change(fieldName, [], operations);
    },
    // ------------------------------------------
    // FIELD META STATE
    // ------------------------------------------
    setFieldDisabledState: (fieldName, value) => {
      log("setFieldDisabledState", fieldName, value);
      if (isStrictMode() && !store.fields[fieldName]) {
        throw new Error(
          `Trying to write the disabled state for an unknown field: ${String(
            fieldName
          )} with strict mode enabled. This is a no-op.`
        );
      }
      if (isDynamicMode()) {
        if (!store.fields[fieldName]) {
          throw new Error(
            `Trying to write the disabled state for an unknown field: ${String(
              fieldName
            )} with strict mode enabled. Dynamic mode not supported yet.`
          );
        }
      }
      const prevDisabled = store.fields[fieldName].disabled;
      store.fields = produce(store.fields, (draft) => {
        draft[fieldName].disabled = value;
      });
      if (prevDisabled !== store.fields[fieldName].disabled) {
        notifyFieldSubscriber(fieldName);
        notifyFormSubscribers();
      }
    },
    setFieldVisibleState: (fieldName, value) => {
      log("setFieldVisibleState", fieldName, value);
      if (isStrictMode() && !store.fields[fieldName]) {
        throw new Error(
          `Trying to write the visible state for an unknown field: ${String(
            fieldName
          )} with strict mode enabled. This is a no-op.`
        );
      }
      if (isDynamicMode()) {
        if (!store.fields[fieldName]) {
          throw new Error(
            `Trying to write the visible state for an unknown field: ${String(
              fieldName
            )} with strict mode enabled. Dynamic mode not supported yet.`
          );
        }
      }
      const prevVisible = store.fields[fieldName].visible;
      store.fields = produce(store.fields, (draft) => {
        draft[fieldName].visible = value;
      });
      if (prevVisible !== store.fields[fieldName].visible) {
        notifyFieldSubscriber(fieldName);
        notifyFormSubscribers();
      }
    },
    setFieldRequiredState: (fieldName, value) => {
      log("setFieldRequiredState", fieldName, value);
      if (isStrictMode() && !store.fields[fieldName]) {
        throw new Error(
          `Trying to write the required state for an unknown field: ${String(
            fieldName
          )} with strict mode enabled. This is a no-op.`
        );
      }
      if (isDynamicMode()) {
        if (!store.fields[fieldName]) {
          throw new Error(
            `Trying to write the required state for an unknown field: ${String(
              fieldName
            )} with strict mode enabled. Dynamic mode not supported yet.`
          );
        }
      }
      store.fields = produce(store.fields, (draft) => {
        draft[fieldName].required = value;
        draft[fieldName].status = "indeterminate";
      });
      notifyFieldSubscriber(fieldName);
      refreshFormStatus();
      notifyFormSubscribers();
    },
    // ------------------------------------------
    // MUTATIONS
    // ------------------------------------------
    addFormMutation: (id2, mutation) => {
      if (formMutations.has(id2)) {
        throw new Error(`Trying to register a Form Mutation with ID: ${id2} but it's already registered.`);
      }
      formMutations.set(id2, mutation);
    },
    removeFormMutation: (id2) => {
      return formMutations.delete(id2);
    },
    removeAllFormMutations: () => {
      const mutationIds = formMutations.keys();
      for (const mutationId of mutationIds) {
        api.removeFormMutation(mutationId);
      }
    },
    addFieldMutation: (fieldName, mutation) => {
      if (isStrictMode() && !store.fields[fieldName]) {
        throw new Error(
          `Trying to register a field-level mutation for an unknown field: ${String(
            fieldName
          )} with strict mode enabled. This is a no-op.`
        );
      }
      if (fieldMutations.has(fieldName)) {
        console.warn(`Overwriting the Form Mutation registered for the field: ${String(fieldName)}.`);
      }
      fieldMutations.set(fieldName, mutation);
    },
    removeFieldMutation: (fieldName) => {
      if (isStrictMode() && !store.fields[fieldName]) {
        throw new Error(
          `Trying to remove a field-level mutation for an unknown field: ${String(
            fieldName
          )} with strict mode enabled. This is a no-op.`
        );
      }
      return fieldMutations.delete(fieldName);
    },
    removeAllFieldMutations: () => {
      const mutationIds = fieldMutations.keys();
      for (const mutationId of mutationIds) {
        api.removeFieldMutation(mutationId);
      }
    },
    addFieldTransform: (fieldName, transform) => {
      if (isStrictMode() && !store.fields[fieldName]) {
        throw new Error(
          `Trying to register a field-level transform for an unknown field: ${String(
            fieldName
          )} with strict mode enabled. This is a no-op.`
        );
      }
      if (fieldTransforms.has(fieldName)) {
        console.warn(`Overwriting the Field Transform registered for the field: ${String(fieldName)}.`);
      }
      fieldTransforms.set(fieldName, transform);
    },
    removeFieldTransform: (fieldName) => {
      if (isStrictMode() && !store.fields[fieldName]) {
        throw new Error(
          `Trying to remove a field-level transform for an unknown field: ${String(
            fieldName
          )} with strict mode enabled. This is a no-op.`
        );
      }
      return fieldTransforms.delete(fieldName);
    },
    removeAllFieldTransforms: () => {
      const transformIds = fieldTransforms.keys();
      for (const transformId of transformIds) {
        api.removeFieldTransform(transformId);
      }
    },
    // ------------------------------------------
    // META STATES
    // ------------------------------------------
    getMeta: () => {
      return store.meta;
    },
    setDisabled: (value) => {
      log("setDisabled", value);
      store.meta = produce(store.meta, (draft) => {
        draft.disabled = value;
      });
      notifyFieldSubscribers(Object.keys(store.fields));
      notifyMetaSubscribers();
      notifyFormSubscribers();
    },
    setSubmitting: (value) => {
      log("setSubmitting", value);
      store.meta = produce(store.meta, (draft) => {
        draft.submitting = value;
      });
      notifyFieldSubscribers(Object.keys(store.fields));
      notifyMetaSubscribers();
      notifyFormSubscribers();
    },
    // ------------------------------------------
    // VALIDATION
    // ------------------------------------------
    clearValidation: (...userFields) => {
      log("clearValidation", userFields);
      const targetFields = userFields.length > 0 ? userFields : Object.keys(store.fields);
      store.errors = produce(store.errors, (draft) => {
        for (const id2 of Object.keys(draft)) {
          const fieldName = draft[id2].field;
          if (targetFields.includes(fieldName)) {
            delete draft[id2];
            errorsByField.set(fieldName, []);
          }
        }
      });
      store.fields = produce(store.fields, (draft) => {
        for (const [fieldName, field] of Object.entries(draft)) {
          if (targetFields.includes(fieldName)) {
            field.status = "indeterminate";
            notifyFieldSubscriber(fieldName);
          }
        }
      });
      refreshFormStatus();
      notifyFormSubscribers();
    },
    validate: (...userFields) => {
      log("validate", userFields);
      const fieldsToValidate = userFields.length > 0 ? userFields : Object.keys(store.fields);
      const fieldsToValidateAndGlobal = [...fieldsToValidate, "global"];
      for (const fieldName of fieldsToValidateAndGlobal) {
        if (isStrictMode() && !store.fields[fieldName]) {
          throw new Error(
            `Trying to validate an unknown field: ${String(fieldName)} with strict mode enabled. This is a no-op.`
          );
        }
        const runningValidations = runningValidationsByField.get(fieldName) ?? /* @__PURE__ */ new Set();
        runningValidationsByField.set(fieldName, runningValidations);
        const validators = validatorsByField.get(fieldName);
        if (!validators) continue;
        const validationTicket = performance.now();
        for (const validator of validators) {
          const validatorFieldName = fieldName === "global" ? void 0 : fieldName;
          const validation = validator(api, validatorFieldName);
          if (!validation) continue;
          const validatorId = validatorIdByFunction.get(validator);
          if (!validatorId) {
            throw new Error(`Impossible to retrieve validator ID for function: ${validator.toString()}`);
          }
          if (validation instanceof Promise) {
            activeAsyncValidation.set(validator, validationTicket);
            runningValidations.add(validatorId);
            if (fieldName === "global") {
              globalValidationRunning = true;
            } else {
              store.fields = produce(store.fields, (draft) => {
                draft[fieldName].validating = true;
              });
              notifyFieldSubscriber(fieldName);
            }
            refreshFormMetaValidatingState();
            let affectedFields = [];
            validation.then((validationResult) => {
              const latestValidationTicket = activeAsyncValidation.get(validator);
              if (latestValidationTicket !== validationTicket) return;
              if (validationResult) {
                if (__DEV__ && validationResult.hasOwnProperty(validatorId)) {
                  console.warn(
                    `[Form State] Using a validation function UID as an error ID is a noop. (${validatorId})`
                  );
                }
                const val = {
                  ...validationResult,
                  [validatorId]: null
                };
                affectedFields = updateStoreErrors(val);
              } else {
                const val = { [validatorId]: null };
                affectedFields = updateStoreErrors(val);
              }
              runningValidations.delete(validatorId);
              if (runningValidations.size === 0) {
                if (fieldName === "global") {
                  globalValidationRunning = false;
                } else {
                  store.fields = produce(store.fields, (draft) => {
                    draft[fieldName].validating = false;
                  });
                }
              }
              activeAsyncValidation.delete(validator);
              if (fieldName === "global") {
                refreshFieldsStatus(affectedFields, true);
                notifyFieldSubscribers(affectedFields);
              } else {
                refreshFieldsStatus([fieldName], true);
                notifyFieldSubscriber(fieldName);
              }
              refreshFormMetaValidatingState();
              refreshFormStatus();
              notifyFormSubscribers();
              if (!store.meta.validating) {
                resolvePendingValidation(store.meta.status === "valid");
              }
            }).catch((reason) => {
              const latestValidationTicket = activeAsyncValidation.get(validator);
              if (latestValidationTicket !== validationTicket) return;
              const errorMessage = typeof reason === "string" ? reason : reason instanceof Error ? reason.message : "unknown";
              const error = {
                id: validatorId,
                field: fieldName,
                message: errorMessage
              };
              const affectedFields2 = updateStoreErrors({ [validatorId]: error });
              runningValidations.delete(validatorId);
              if (runningValidations.size === 0) {
                if (fieldName === "global") {
                  globalValidationRunning = false;
                } else {
                  store.fields = produce(store.fields, (draft) => {
                    const field = draft[fieldName];
                    if (field) field.validating = false;
                  });
                }
              }
              activeAsyncValidation.delete(validator);
              if (fieldName === "global") {
                refreshFieldsStatus(affectedFields2, true);
                notifyFieldSubscribers(affectedFields2);
              } else {
                refreshFieldsStatus([fieldName], true);
                notifyFieldSubscriber(fieldName);
              }
              refreshFormMetaValidatingState();
              refreshFormStatus();
              notifyFormSubscribers();
              if (!store.meta.validating) {
                resolvePendingValidation(store.meta.status === "valid");
              }
            });
          } else {
            if (__DEV__ && validation.hasOwnProperty(validatorId)) {
              console.warn(`[Form State] Using a validation function UID as an error ID is a noop. (${validatorId})`);
            }
            const affectedFields = updateStoreErrors(validation);
            if (fieldName === "global") {
              const globalFieldsToNotify = affectedFields.filter((x) => !fieldsToValidate.includes(x)).concat(fieldsToValidate.filter((x) => !affectedFields.includes(x)));
              refreshFieldsStatus(globalFieldsToNotify);
            } else {
              notifyFieldSubscriber(fieldName);
            }
          }
        }
      }
      refreshFieldsStatus(fieldsToValidate);
      refreshFormStatus();
      notifyFormSubscribers();
      return waitForValidation();
    },
    waitForValidation: () => {
      return store.meta.validating ? waitForValidation().then(() => {
      }) : Promise.resolve();
    },
    getValidator: (id2) => {
      return validatorsById.get(id2);
    },
    addValidation: (id2, validator, fieldName) => {
      if (isStrictMode() && (!!fieldName && !store.fields[fieldName] || !fieldName)) {
        throw new Error(
          `Trying to register a field-level validation for an unknown field: ${String(
            fieldName
          )} with strict mode enabled. This is a no-op.`
        );
      }
      if (validatorsById.has(id2)) {
        throw new Error(`Trying to register a validator with ID: ${id2} but it's already registered.`);
      }
      validatorsById.set(id2, validator);
      validatorIdByFunction.set(validator, id2);
      if (fieldName === void 0) {
        validatorsByField.set("global", validatorsByField.get("global") ?? /* @__PURE__ */ new Set());
        validatorsByField.get("global")?.add(validator);
      } else {
        validatorsByField.set(fieldName, validatorsByField.get(fieldName) ?? /* @__PURE__ */ new Set());
        validatorsByField.get(fieldName)?.add(validator);
      }
    },
    removeValidation: (id2) => {
      const validator = validatorsById.get(id2);
      validatorsById.delete(id2);
      if (!validator) return false;
      validatorIdByFunction.delete(validator);
      for (const [, validators] of validatorsByField) {
        validators.delete(validator);
      }
      return true;
    },
    removeAllValidations: () => {
      const validations = validatorsById.keys();
      for (const validation of validations) {
        api.removeValidation(validation);
      }
    },
    // ------------------------------------------
    // SUBSCRIPTIONS
    // ------------------------------------------
    subscribeToField: (fieldName, subscriber) => {
      if (isStrictMode()) {
        throwIfUnknownField(store, fieldName);
      }
      const subscription = [fieldName, subscriber];
      fieldSubscribers.add(subscription);
      return () => {
        fieldSubscribers.delete(subscription);
      };
    },
    subscribeToForm: (subscriber) => {
      formSubscribers.add(subscriber);
      return () => {
        formSubscribers.delete(subscriber);
      };
    },
    subscribeToMeta: (subscriber) => {
      formMetaSubscribers.add(subscriber);
      return () => {
        formMetaSubscribers.delete(subscriber);
      };
    },
    removeAllListeners: () => {
      formSubscribers.clear();
      fieldSubscribers.clear();
      formMetaSubscribers.clear();
    }
  };
  if (config?.fieldMutations) {
    for (const { field, mutation } of config.fieldMutations) {
      api.addFieldMutation(field, mutation);
    }
  }
  if (config?.fieldTransforms) {
    for (const { field, transform } of config.fieldTransforms) {
      api.addFieldTransform(field, transform);
    }
  }
  if (config?.formMutations) {
    for (const { id: id2, mutation } of config.formMutations) {
      api.addFormMutation(id2, mutation);
    }
  }
  if (config?.validations) {
    for (const { id: id2, validator, fields } of config.validations) {
      api.addValidation(id2, validator, fields);
    }
  }
  return {
    api,
    store
  };
}
export {
  createForm
};
//# sourceMappingURL=createForm.js.map
